import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import {
  ReactNode, useEffect, useRef, useState,
} from 'react';
import { ThemeType, useWindowDimensions } from '../../styling';
import { Flex, FlexColumn } from '../Flex';
import { Tooltip, TooltipInfoIcon, useCreateAndAddRefs } from '../../..';

const CssWrapper = styled.div`
  width: 100%;
  padding: 0 !important;
`;

export const PaddingCollapsibleBodyContent = styled.div`
${({ theme }) => `
  padding: ${theme.padding[5]} calc(${theme.padding[11]} + ${theme.padding[2]}) ${theme.padding[7]} calc(${theme.padding[11]} + ${theme.padding[2]});
`}`;

export const CollapsibleWrapper = styled.div<{ maxHeight: number, level?: number }>`
  width: 100%;
  background-color: ${({ theme }) => theme.textSecondary};
  border-radius: ${({ theme }) => theme.padding[2]};
  box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06);
  
  ${({ level, theme }) => (level === 0 ? `
    margin-bottom: ${theme.padding[6]};
  ` : '')}

  ${({ level }) => (level === 1 || level === 2 ? `
    margin-bottom: 0;
    box-sizing: border-box;
    box-shadow: none;
  ` : '')}
`;

const BodyWrapper = styled(FlexColumn) <{ css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  position:relative;
  width: 100%;
  ${({ css }) => css};
`;

export const CollapsibleBodyButtons = styled(Flex) <{ level?: number }>`
  justify-content: flex-end;
  gap: ${({ theme }) => theme.padding[4]};
  padding: ${({ theme }) => theme.padding[5]};
`;

export const CollapsibleHeader = styled.div<{
  collapsed: boolean,
  css?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  level?: number,
  hasSubHeader?: boolean,
}>`
  ${({
    theme, collapsed, level, hasSubHeader,
  }) => `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    width: 100%;
    padding: ${theme.padding[3]} ${theme.padding[6]};
    border-radius: ${(() => {
    if (level === 1) return theme.borderRadius[2];
    return `${theme.padding[2]} ${theme.padding[2]} ${collapsed ? theme.padding[2] : '0 0'}`;
  })()};

    border-radius: ${theme.padding[2]} ${theme.padding[2]} ${collapsed ? theme.padding[2] : '0 0'};


    box-sizing: border-box;
    background-color: ${theme.colours.primary[1]};
    box-shadow: 0px ${theme.borderWidth[1]} 0px ${theme.neutralDark};
    line-height: ${theme.fontSize[6]};
    position: relative;

    ${level === 0 ? `
      min-height: ${theme.fontSize[10]};
      box-shadow: 0px ${theme.borderWidth[1]} 0px ${theme.neutralDark};
    ` : ''}

    ${level === 1 ? `
      min-height: ${theme.fontSize[10]};
      box-shadow: none;
      background-color: ${theme.colours.primary[11]};

      ${(() => (collapsed ? `
        box-shadow: none;
      ` : ''))()}
    ` : ''}

    ${level === 2 ? `
      box-shadow: none;
      padding: ${theme.padding[2]} ${theme.padding[6]};
      
      background-color: ${theme.colours.neutral[8]};
      border-radius: ${theme.borderRadius[2]} ${theme.borderRadius[2]} 0 0;
    ` : ''}

    ${collapsed ? `border-radius: ${theme.borderRadius[2]};` : ''}
    ${hasSubHeader ? `border-radius: ${theme.borderRadius[2]} ${theme.borderRadius[2]} 0 0;` : ''}
  `};
  ${({ css }) => css};
`;

export const CollapsibleBody = styled.div<{
  collapsed?: boolean,
  css?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  level?: number,
}>`
  ${({
    theme,
    collapsed,
  }) => `
    height: auto;
    background-color: ${theme.textSecondary};
    border-radius: 0 0 ${theme.borderRadius[2]} ${theme.borderRadius[2]};
    overflow: inherit;

    padding-top: ${theme.padding[4]};
    padding-bottom: ${theme.padding[4]};

    ${collapsed && `
      padding: 0 !important; 
      overflow: hidden; 
      height: 0;
    `};
  `};
  ${({ css }) => css};
`;

export const CollapsibleTitle = styled(Flex)<{
  level?: number,
  css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }
>`${({ theme, level }) => `
    align-items: center;
    gap: ${theme.padding[1]};
    font-weight: ${level === 1 || level === 2 || level === 3 ? theme.fontWeight.bold : theme.fontWeight.bolder};
    font-size: ${theme.fontSize[3]};
    color: ${level === 1 ? theme.colours.primary[1] : theme.colours.neutral[10]};
    line-height: ${theme.fontSize[7]};
    width: 100%;

    ${!level ? `
      letter-spacing: 0.2px;
      font-family: 'Montserrat', sans-serif;
    ` : ''}

    ${level === 1 ? `
      font-size: ${theme.fontSize[2]};
      line-height: ${theme.fontSize[6]};
    ` : ''}

    ${level === 2 || level === 3 ? `
      color: ${theme.colours.neutral[2]};
      font-size: ${theme.fontSize[2]};
    ` : ''}
  `};
  ${({ css }) => css};
`;

const CollapsibleIcon = styled.i<{ collapsed: boolean, level?: number }>` ${({ theme, level }) => `
  color: ${theme.textSecondary};  
  ${level === 1 ? `color: ${theme.colours.primary[1]};` : ''}
  ${level === 2 || level === 3 ? `color: ${theme.colours.neutral[2]};` : ''}
  font-size: ${theme.fontSize[3]};
  margin-left: ${theme.padding[8]};
  cursor: pointer;
`}`;

const HeaderLeft = styled(Flex)`
  align-items: center;
  width: 100%;
`;

const HeaderRight = styled(Flex)`
  align-items: center;
`;

const SubHeader = styled.div`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const IconWrapper = styled.div`
  margin-right: ${({ theme }) => theme.padding[3]};
`;

export interface CollapsibleProps {
  id?: string,
  refName?: string,
  title?: ReactNode,
  children: ReactNode,
  collapsible?: boolean,
  collapsed?: boolean,
  groupButtons?: ReactNode[],
  onClickCollapse?: (collapsed: boolean) => void,
  titleIcon?: ReactNode | null,
  headerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  titleCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  bodyCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  bodyContentCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  level?: number,
  tooltip?: ReactNode,
  subHeader?: (collapsed: boolean) => ReactNode,
  iconInverted?: boolean,
}

export function Collapsible({
  id,
  refName = '',
  title,
  children,
  collapsible = true,
  collapsed = false,
  groupButtons,
  onClickCollapse = () => { },
  titleIcon,
  titleCss,
  headerCss,
  bodyCss,
  bodyContentCss,
  level = 0,
  subHeader,
  tooltip = '',
  iconInverted,
}: Readonly<CollapsibleProps>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowDimensions();
  const refByRefName = useCreateAndAddRefs(refName);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsed);
  const tooltipId = `tooltip-${id}`;

  const toggle = () => {
    const collapse = collapsible && !isCollapsed;
    onClickCollapse(collapse);
    setIsCollapsed(collapse);
  };

  useEffect(() => {
    setMaxHeight(contentRef?.current?.scrollHeight ?? 0);
  }, [contentRef, contentRef?.current, contentRef?.current?.offsetHeight, contentRef?.current?.scrollHeight, windowSize]);

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  return (
    <CssWrapper id={id} ref={refByRefName}>
      <CollapsibleWrapper
        id={`${id}-wrapper`}
        maxHeight={maxHeight}
        className="collapsible"
        level={level}
      >
        {title && (
          <CollapsibleHeader
            id={`${id}-header`}
            collapsed={isCollapsed}
            className="collapsible__header"
            css={headerCss}
            level={level}
            onClick={toggle}
            hasSubHeader={!!subHeader}
          >
            <HeaderLeft>
              {titleIcon && <IconWrapper>{titleIcon}</IconWrapper>}
              <CollapsibleTitle level={level} css={titleCss}>
                {title}
                {tooltip && (
                <>
                  <TooltipInfoIcon id={tooltipId} tabIndex={-1} iconInverted={iconInverted} />
                  <Tooltip id={tooltipId} place="top">{tooltip}</Tooltip>
                </>
                )}
              </CollapsibleTitle>
            </HeaderLeft>
            {(groupButtons || collapsible) && (
              <HeaderRight>
                <Flex>
                  {groupButtons}
                </Flex>
                {collapsible && (
                  <CollapsibleIcon
                    level={level}
                    className={isCollapsed ? 'mi-chevron-down' : 'mi-chevron-up'}
                    collapsed={isCollapsed}
                  />
                )}
              </HeaderRight>
            )}
          </CollapsibleHeader>
        )}
        {subHeader && (
          <SubHeader>
            {subHeader(isCollapsed)}
          </SubHeader>
        )}
        <CollapsibleBody
          ref={contentRef}
          collapsed={isCollapsed}
          css={bodyCss}
          level={level}
        >
          <BodyWrapper css={bodyContentCss}>
            {children}
          </BodyWrapper>
        </CollapsibleBody>
      </CollapsibleWrapper>
    </CssWrapper>
  );
}
